import { _, helpers, mui, React, ts, ui, useSelector } from '_core';

import { prepareBucketedEventStudyData } from './helpers';
import { Params } from './types';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  fileEvent: string;
  fullScreen: boolean;
  params: Params;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, fileEvent, params, fullScreen, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [selectedTab, setSelectedTab] = React.useState<'error' | 'tstat'>('error');

  const resources = useSelector((state) => state.resources);
  const [showError, setShowError] = React.useState(false);

  const loadData = async (selectedTab: 'error' | 'tstat') => {
    try {
      const response = await loadWidgetData({
        data: {
          file: fileEvent,
          rename: selectedTab == 'error' ? { avg_cum: 'average_cumulative_return', std_cum: 'error' } : {},
          columns: selectedTab == 'tstat' ? ['category', 'days_from_event', 'tstat'] : undefined,
        },
      });
      if (response) setData(response.data);
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (fileEvent) loadData(selectedTab);
    return () => setData(null);
  }, [fileEvent, selectedTab]);

  const getTabs = () => (
    <mui.core.Box display="flex">
      <mui.core.Box flexGrow={1} display="flex" alignItems="flex-end">
        <mui.core.Tabs
          indicatorColor="primary"
          textColor="primary"
          value={selectedTab}
          onChange={(_e, newTab) => setSelectedTab(newTab)}
        >
          <mui.core.Tab label="Cumulative Returns" value="error" />
          <mui.core.Tab label="T-stat" value="tstat" />
        </mui.core.Tabs>
      </mui.core.Box>

      {selectedTab == 'error' && (
        <mui.core.Box display="flex" justifyContent="flex-end">
          <mui.core.FormControlLabel
            control={<mui.core.Switch size="small" checked={showError} onChange={() => setShowError((v) => !v)} />}
            label={showError ? 'Hide error bars' : 'Show error bars'}
          />
        </mui.core.Box>
      )}
    </mui.core.Box>
  );

  const renderChart = () => {
    if (selectedTab == 'error') {
      return (
        <ui.ErrorChart
          id={id}
          data={data}
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          xAxisField="days_from_event"
          yAxisField="average_cumulative_return"
          xAxisLabel="Days"
          yAxisLabel="Mean Cumulative Returns (%)"
          exportTitle={title}
          bucket="category"
          showError={showError}
          showBucketOnTooltip
          formatData={(errorData) => prepareBucketedEventStudyData(errorData, selectedTab, params, resources) as any}
        />
      );
    }
    if (selectedTab == 'tstat') {
      const currentData = prepareBucketedEventStudyData(
        data as ts.types.widgets.HeatMapData,
        selectedTab,
        params,
        resources
      );

      return (
        <ui.HeatmapChart
          id={id}
          data={currentData as ts.types.widgets.HeatMapData}
          xAxisField="days_from_event"
          yAxisField="category"
          exportTitle={title}
          fullScreen={fullScreen}
          readOnly={readOnly}
        />
      );
    }

    return null;
  };

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      {getTabs()}
      <mui.core.Box flexGrow={1}>{!_.isNil(data) && renderChart()}</mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
